import { useEffect, useState, useMemo } from "react";
import { Building2, Check, ChevronDown, Search } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useTenants } from "../../hooks/useTenants";
import { myLocalStorage } from "../StorageHelper";

export default function TenantLists({
  onSelectorChange,
  isLabelNeeded = true,
  variant = "default",
}: {
  onSelectorChange?: (value: string) => void;
  isLabelNeeded?: boolean;
  variant?: "default" | "card";
}) {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const { tenants, loading } = useTenants();
  const cachedTenantName =
    myLocalStorage.getItem("latestTenant")?.tenantName
    || myLocalStorage.getItem("latestTenant")
    || "";

  useEffect(() => {
    if (cachedTenantName && !selectedOption) {
      setSelectedOption(cachedTenantName);
      onSelectorChange?.(cachedTenantName);
    }
  }, [cachedTenantName, onSelectorChange, selectedOption]);

  useEffect(() => {
    if (tenants.length > 0) {
      const latestTenant = myLocalStorage.getItem("latestTenant");

      const existingTenant = tenants.find(
        (tenant) =>
          tenant.tenantName === latestTenant?.tenantName ||
          tenant.tenantName === latestTenant
      );

      if (existingTenant) {
        myLocalStorage.setItem("latestTenant", existingTenant);
        setSelectedOption(existingTenant.tenantName);
        onSelectorChange?.(existingTenant.tenantName);
      } else {
        const firstTenant = tenants[0];
        setSelectedOption(firstTenant.tenantName);
        onSelectorChange?.(firstTenant.tenantName);
        myLocalStorage.setItem("latestTenant", firstTenant);
      }
    }
  }, [tenants]);

  const displayTenantName = selectedOption || cachedTenantName;
  const isCard = variant === "card";

  const handleSelectorChange = (tenant: any) => {
    setSelectedOption(tenant.tenantName);
    onSelectorChange?.(tenant.tenantName);
    myLocalStorage.setItem("latestTenant", tenant);
  };

  const filteredTenants = useMemo(() => {
    const term = search.toLowerCase();
    return tenants.filter((tenant) =>
      tenant.tenantName.toLowerCase().includes(term)
    );
  }, [tenants, search]);

  if (loading && tenants.length === 0 && !cachedTenantName) {
    return <div>Loading...</div>;
  }
  if (!loading && tenants.length === 0 && !cachedTenantName) {
    return <div>No tenants found</div>;
  }

  return (
    <div className={isCard ? "w-full min-w-0 sm:w-[240px] sm:shrink-0" : "flex items-start gap-4 flex-wrap"}>
      <div className={isCard ? "relative flex flex-col gap-1.5" : "relative"}>
        {isLabelNeeded && (
          <label
            htmlFor="tenant-selector"
            className={isCard
              ? "text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1"
              : "absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"}
          >
            {isCard && <Building2 className="w-3.5 h-3.5" />}
            {isCard ? "Tenant" : "Tenant List"}
          </label>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              id="tenant-selector"
              variant="outline"
              className={isCard
                ? "w-full group flex items-center justify-between px-4 py-2.5 rounded-xl bg-card border border-border/50 hover:border-sky-500/30 transition-all duration-300 shadow-sm cursor-pointer"
                : "min-w-[220px] justify-between rounded-md"}
              data-testid="dropdown-selector"
            >
              <span className={isCard ? "text-[14px] font-medium text-foreground group-hover:text-primary transition-colors truncate" : undefined}>
                {displayTenantName || "Select tenant"}
              </span>
              <ChevronDown className={isCard
                ? "h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-y-0.5 flex-shrink-0 ml-2"
                : "w-4 h-4 ml-2"} />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className={isCard
            ? "min-w-[240px] max-h-[300px] overflow-y-auto bg-popover/95 backdrop-blur-xl border border-border/50 shadow-xl shadow-sky-500/10 rounded-xl p-1.5 space-y-0.5"
            : "min-w-[220px] max-h-[300px] overflow-y-auto"}>
            <div className={isCard
              ? "flex items-center gap-2 px-3 py-2 sticky top-0 bg-popover border-b border-border/50"
              : "flex items-center px-2 py-1.5 sticky top-0 bg-background border-b"}>
              <Search className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenant..."
                className="w-full text-sm bg-transparent outline-none"
              />
            </div>

            {filteredTenants.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                No tenants found
              </div>
            ) : (
              filteredTenants.map((tenant) => (
                <DropdownMenuItem
                  key={tenant.domainName}
                  onClick={() => handleSelectorChange(tenant)}
                  className={isCard
                    ? `flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${tenant.tenantName === displayTenantName
                      ? "bg-gradient-to-r from-primary/20 to-primary/10 text-sky-500"
                      : "hover:bg-accent/50 text-foreground font-medium"}`
                    : undefined}
                >
                  <span className={isCard ? "flex-1 truncate font-medium" : undefined}>{tenant.tenantName}</span>
                  {isCard && tenant.tenantName === displayTenantName && (
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 flex-shrink-0 shadow-sm">
                      <Check className="h-3 w-3 text-primary-foreground" />
                    </span>
                  )}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
